// =============================================================================
//  MOTOR DE CÁLCULO v2 — Teste das Estações do Coração
//  "O Coração de Todas as Coisas" — por Magan
//  Fonte ÚNICA de cálculo. Substitui prepareResult() e os limiares espalhados.
//  Mantém campos legados (compatibilidade) e adiciona as variáveis novas.
//  Uso:  import { computeResult } from './scoringEngine';
//        import { questions } from '../data/questionsData';
//        const result = computeResult(answers, questions);   // answers = {id:1..5}
// =============================================================================

export const LIMIARES = {
  RAIZ_QUENTE: 60, RAIZ_FRIA: 40,
  COMBO_EMERGENTE: 50, COMBO_ATIVA: 70, COMBO_GATE_MIN: 45,
  TRANSICAO_MARGEM: 6, INTEIRO_SPREAD_MAX: 15, INTEIRO_PISO: 58,
  TENSAO_MODERADA: 18, TENSAO_FORTE: 28, CONSISTENCIA_MIN: 60,
};
export const ROOTS = ['peregrino', 'guardiao', 'amante', 'alquimista'];
export const ROOT_LABEL = { peregrino:'Peregrino', guardiao:'Guardião', amante:'Amante', alquimista:'Alquimista' };
export const COMBO_PARENTS = {
  visionario:['peregrino','alquimista'], encontro:['peregrino','amante'], guerreiro:['peregrino','guardiao'],
  curador:['amante','guardiao'], guardiao_sabio:['guardiao','alquimista'], poeta:['amante','alquimista'],
  profeta:['peregrino','amante','alquimista'], construtor:['peregrino','guardiao','alquimista'],
  companheiro:['peregrino','guardiao','amante'], sabio_compassivo:['guardiao','amante','alquimista'],
};
export const COMBO_LABEL = {
  visionario:'Visionário', encontro:'Peregrino do Encontro', guerreiro:'Guerreiro', curador:'Curador',
  guardiao_sabio:'Guardião Sábio', poeta:'Poeta', profeta:'Profeta', construtor:'Construtor',
  companheiro:'Companheiro de Jornada', sabio_compassivo:'Sábio Compassivo',
};
const DUALS = ['visionario','encontro','guerreiro','curador','guardiao_sabio','poeta'];
const TRIPLES = ['profeta','construtor','companheiro','sabio_compassivo'];

const clamp = (x,a,b)=>Math.max(a,Math.min(b,x));
const mean = (a)=>a.reduce((s,x)=>s+x,0)/(a.length||1);
const toPct = (sum,n)=> n===0?0:((sum-n)/(4*n))*100;

export function computeResult(answers, bank) {
  const acc = {};
  const bump = (axis,v)=>{ if(v==null) return; (acc[axis]??={sum:0,n:0}); acc[axis].sum+=v; acc[axis].n+=1; };
  let answered=0, total=0; const attention=[], fwd=[], rev=[], pairs={};

  for (const item of bank) {
    const axis = item.axis || item.category;
    if (item.kind === 'attention') { attention.push({ ok: answers[item.id]===item.expect, answered: answers[item.id]!=null }); continue; }
    total += 1;
    const raw = answers[item.id];
    if (raw != null) answered += 1;
    const v = raw==null ? null : (item.reverse ? 6-raw : raw);
    bump(axis, v);
    if (raw != null) (item.reverse ? rev : fwd).push(item.reverse ? 6-raw : raw);
    if (item.pairId && raw != null) (pairs[item.pairId] ??= []).push(v);
  }

  const roots = {};
  for (const k of ROOTS){ const a=acc[k]||{sum:0,n:0}; roots[k]={ raw:a.sum, n:a.n, pct:clamp(Math.round(toPct(a.sum,a.n)),0,100) }; }

  const levelOf=(score,gmin)=> gmin<LIMIARES.COMBO_GATE_MIN?'latente':(score>=LIMIARES.COMBO_ATIVA?'ativa':(score>=LIMIARES.COMBO_EMERGENTE?'emergente':'latente'));
  function comboScore(key){
    const a=acc[key]||{sum:0,n:0}; const itemsPct=toPct(a.sum,a.n);
    const par=COMBO_PARENTS[key].map(p=>roots[p].pct); const gavg=mean(par), gmin=Math.min(...par);
    let s=0.6*itemsPct+0.4*gavg; s=Math.min(s,gmin+15);
    return { key, pct:clamp(Math.round(s),0,100), itemsPct:Math.round(itemsPct), gateMin:Math.round(gmin), level:levelOf(s,gmin), raw:a.sum };
  }
  const combos={}; [...DUALS,...TRIPLES].forEach(k=>{combos[k]=comboScore(k);});

  const sorted=[...ROOTS].sort((a,b)=>roots[b].pct-roots[a].pct); const r1=sorted[0], r2=sorted[1];
  const spread=roots[sorted[0]].pct-roots[sorted[3]].pct;
  const allAlive=ROOTS.every(k=>roots[k].pct>=LIMIARES.INTEIRO_PISO);
  const coracaoInteiro=allAlive && spread<=LIMIARES.INTEIRO_SPREAD_MAX;

  let dominant;
  if (coracaoInteiro) dominant={ type:'coracao_inteiro', key:'coracao_inteiro', label:'O Coração Inteiro' };
  else if (roots[r1].pct-roots[r2].pct <= LIMIARES.TRANSICAO_MARGEM){
    const [aa,bb]=[r1,r2].slice().sort();
    dominant={ type:'transition', key:`${aa}__${bb}`, pair:[aa,bb], roots:[r1,r2], label:`Transição: ${ROOT_LABEL[aa]} ⇄ ${ROOT_LABEL[bb]}` };
  } else dominant={ type:'root', key:r1, label:`O ${ROOT_LABEL[r1]}` };

  const anchor = dominant.type==='root' ? [dominant.key] : dominant.type==='transition' ? dominant.roots : ROOTS;
  const eligible=[...DUALS,...TRIPLES]
    .filter(k=>combos[k].level!=='latente')
    .filter(k=>anchor.some(r=>COMBO_PARENTS[k].includes(r)))
    .sort((a,b)=>combos[b].pct-combos[a].pct || COMBO_PARENTS[a].length-COMBO_PARENTS[b].length || a.localeCompare(b));
  const expression = eligible.length ? { key:eligible[0], ...combos[eligible[0]], label:COMBO_LABEL[eligible[0]] } : null;

  let displayKey;
  if (dominant.type==='coracao_inteiro') displayKey='coracao_inteiro';
  else if (expression && expression.level==='ativa' && dominant.type==='root') displayKey=expression.key;
  else if (dominant.type==='root') displayKey=dominant.key;
  else displayKey=r1;

  const seasons={hot:[],warm:[],cold:[]};
  for(const k of ROOTS){ const p=roots[k].pct; const b=p>=LIMIARES.RAIZ_QUENTE?'hot':p<LIMIARES.RAIZ_FRIA?'cold':'warm'; seasons[b].push({key:k,label:ROOT_LABEL[k],pct:p}); }
  seasons.hot.sort((a,b)=>b.pct-a.pct); seasons.cold.sort((a,b)=>a.pct-b.pct); seasons.warm.sort((a,b)=>b.pct-a.pct);

  const tensions=[];
  for(let i=0;i<ROOTS.length;i++) for(let j=i+1;j<ROOTS.length;j++){
    const A=ROOTS[i],B=ROOTS[j],diff=Math.abs(roots[A].pct-roots[B].pct);
    if(diff>=LIMIARES.TENSAO_MODERADA){ const strong=roots[A].pct>=roots[B].pct?A:B;
      tensions.push({ pair:[A,B], diff, intensity:diff>=LIMIARES.TENSAO_FORTE?'forte':'moderada', dominant:strong, shadowed:strong===A?B:A }); }
  }
  tensions.sort((a,b)=>b.diff-a.diff);

  const completeness=total?Math.round((answered/total)*100):0;
  const acqGap=(fwd.length&&rev.length)?Math.abs(mean(fwd)-mean(rev)):0;
  const pairDists=Object.values(pairs).filter(p=>p.length===2).map(([x,y])=>Math.abs(x-y));
  const consistency=pairDists.length?clamp(Math.round(100-(mean(pairDists)/4)*100),0,100):null;
  const attnFailed=attention.filter(a=>a.answered&&!a.ok).length;
  let confScore=completeness; if(consistency!=null) confScore=Math.round(0.6*consistency+0.4*completeness);
  confScore=clamp(Math.round(confScore-acqGap*8-attnFailed*20),0,100);
  const confidence={ score:confScore, completeness, consistency, acquiescenceGap:+acqGap.toFixed(2), attentionFailed:attnFailed,
    flag: confScore>=75?'alta':confScore>=LIMIARES.CONSISTENCIA_MIN?'media':'baixa' };

  return {
    roots, combos, dominant, expression, displayKey, coracaoInteiro,
    balance:{ spread, state: spread<=LIMIARES.INTEIRO_SPREAD_MAX?'integro':spread<=30?'quase':'fragmentado' },
    seasons, tensions, confidence,
    // ----- compatibilidade com componentes existentes -----
    mainArchetypes: Object.fromEntries(ROOTS.map(k=>[k,roots[k].raw])),
    dualCombinations: Object.fromEntries(DUALS.map(k=>[k,combos[k].raw])),
    tripleCombinations: Object.fromEntries(TRIPLES.map(k=>[k,combos[k].raw])),
    dominantArchetype: displayKey,
    isCoracaoInteiro: coracaoInteiro,
  };
}
export default computeResult;
